<template>
  <span class="countup">{{ display }}</span>
</template>

<script setup>
import { ref, watch, onMounted, onBeforeUnmount } from "vue";

const props = defineProps({
  value: { type: Number, default: 0 },
  duration: { type: Number, default: 900 },
  decimals: { type: Number, default: 0 },
});

const display = ref(format(0));
let raf = null;
let current = 0;

function format(n) {
  return Number(n).toLocaleString(undefined, {
    minimumFractionDigits: props.decimals,
    maximumFractionDigits: props.decimals,
  });
}

// easeOutExpo — fast start, gentle settle
function ease(t) {
  return t === 1 ? 1 : 1 - Math.pow(2, -10 * t);
}

function animate(to) {
  if (raf) cancelAnimationFrame(raf);

  const reduce =
    typeof window !== "undefined" &&
    window.matchMedia &&
    window.matchMedia("(prefers-reduced-motion: reduce)").matches;

  if (reduce || props.duration <= 0) {
    current = to;
    display.value = format(to);
    return;
  }

  const from = current;
  const start = performance.now();

  const tick = (now) => {
    const t = Math.min(1, (now - start) / props.duration);
    current = from + (to - from) * ease(t);
    display.value = format(current);
    if (t < 1) raf = requestAnimationFrame(tick);
    else {
      current = to;
      display.value = format(to);
      raf = null;
    }
  };
  raf = requestAnimationFrame(tick);
}

watch(() => props.value, (v) => animate(v));
onMounted(() => animate(props.value));
onBeforeUnmount(() => raf && cancelAnimationFrame(raf));
</script>

<style scoped>
.countup {
  font-variant-numeric: tabular-nums;
  font-feature-settings: "tnum" 1;
}
</style>
